import { z } from 'zod';
import { Slug, Ulid } from './common.js';
import { pieceModelOf } from './piece-models.js';
import { PLAY_SERVICES, PlayCatalogSource } from './play.js';
/**
 * Publishing and promotion as background jobs (`docs/game-events-plan.md` §6.5).
 *
 * A playground publish and a rebuild-on-promote both take minutes, so both are jobs with one
 * server-side record, `publish_jobs`, and one row per sibling build, `publish_job_builds`. What
 * happened is a list of {@link PublishEvent}s, and **every** surface that says it — the progress
 * panel in the playground bar and on the channel page, the stored log, the CLI, agentd's `status`
 * and a linked agent's `publish` tool — renders them through {@link formatPublishEvent} and nothing
 * else. Four places therefore say one sentence.
 *
 * The rule the format keeps: every line about a build says which pieces it is for. A line that says
 * "uploaded" without a model leaves the reader guessing which of three builds failed.
 */
export const PublishJobKind = z.enum(['publish', 'rebuild', 'copy']);
export const PublishJobStatus = z.enum(['queued', 'running', 'done', 'partial', 'failed']);
/** How far one sibling got. `published` and `failed` are the two ends. */
export const PublishStep = z.enum(['check', 'build', 'pack', 'upload', 'patch', 'published', 'failed']);
/** One sibling of a release, as the job names it. `position` is 1-based; the primary is last. */
export const PublishSibling = z.object({
    pieceModel: z.string().min(1).max(80),
    primary: z.boolean(),
    position: z.number().int().min(1),
});
/** How many lines of each list a publish's merge keeps; the last says how many more there were. */
export const PLAY_MERGED_LINES = 20;
/**
 * What a playground publish did with achievement and leaderboard edits made somewhere else since
 * the project's `boardflight.play.json` last matched BoardFlight (`docs/achievements-plan.md` §5.5):
 * the newest such version and where it was made; a line per thing it brought into the file; and a
 * line per thing both had changed, where the other edit was kept, saying what the file had.
 * `written` is false when the file changed while the publish ran: nothing was written and nothing
 * pushed, and the next publish tries again. No name is kept here — who made the version is on its
 * row in the catalog's history, joined live like every other name.
 */
export const PlayMerged = z.object({
    version: z.number().int().min(1),
    source: PlayCatalogSource,
    brought: z.array(z.string().max(300)).max(PLAY_MERGED_LINES),
    kept: z.array(z.string().max(300)).max(PLAY_MERGED_LINES),
    written: z.boolean(),
});
/**
 * A project catalog that declared something and did not go up beside the build, and why: the
 * server's own sentence where it refused it (a game without the feature included, which names the
 * thing and never a reason), or what stopped it before it was sent. A catalog that stays behind
 * while the build publishes is a game whose achievements or events never reach BoardFlight, so the
 * publish says so rather than leave it to the sandbox's log.
 */
export const CatalogNotPushed = z.object({
    file: z.enum(['boardflight.events.json', 'boardflight.play.json']),
    reason: z.string().max(500),
});
export const PublishEvent = z.discriminatedUnion('type', [
    /** The job's first line: what is being made, where it goes, and the builds it will make. */
    z.object({
        type: z.literal('start'),
        at: z.number().int(),
        kind: PublishJobKind,
        channel: z.string(),
        versionName: z.string().nullable(),
        /** A rebuild's provenance: the deploy it is rebuilt from and the channel it was built for. */
        from: z
            .object({ versionName: z.string(), builtFor: z.string().nullable() })
            .nullable()
            .optional(),
        siblings: z.array(PublishSibling).min(1),
    }),
    /** One sibling reached a step. Carries what the step produced, when it produced something. */
    z.object({
        type: z.literal('step'),
        at: z.number().int(),
        pieceModel: z.string(),
        step: PublishStep,
        buildId: z.string().nullable().optional(),
        /** A copy's source, so its line can name both builds. */
        sourceBuildId: z.string().nullable().optional(),
        versionCode: z.number().int().nullable().optional(),
        bytes: z.number().int().nullable().optional(),
        durationMs: z.number().int().nullable().optional(),
        /** A failure's own words, verbatim. */
        message: z.string().max(4000).nullable().optional(),
    }),
    /** The target channel's models were synced to the release, the way a deploy does. */
    z.object({
        type: z.literal('synced'),
        at: z.number().int(),
        channel: z.string(),
        /** Stems, primary first. */
        models: z.array(z.string()).min(1),
    }),
    /** The end. `partial` names who published; `failed` says why nothing did. */
    z.object({
        type: z.literal('done'),
        at: z.number().int(),
        status: z.enum(['done', 'partial', 'failed']),
        durationMs: z.number().int(),
        channel: z.string(),
        versionName: z.string().nullable(),
        versionCode: z.number().int().nullable().optional(),
        error: z.string().max(4000).nullable().optional(),
        /** A partial result's two lists, as stems — the line names both. */
        published: z.array(z.string()).optional(),
        unpublished: z.array(z.string()).optional(),
        /**
         * The play services the release's catalog declares and the target channel has off
         * (`docs/achievements-plan.md` §8.1), with that channel's id: the line says so, and the
         * dashboard's publish panel turns it into a **Turn on** button. Turning one on is a person's act
         * (`PUT /v1/apps/:appId/play-settings`), so the publish only reports it. Absent when there is
         * nothing to offer.
         */
        playOff: z
            .object({ channelId: z.string().min(1).max(64), services: z.array(z.enum(PLAY_SERVICES)).min(1).max(PLAY_SERVICES.length) })
            .optional(),
        /** The project's catalogs that declared something and did not go up. Absent when none. */
        catalogsNotPushed: z.array(CatalogNotPushed).max(2).optional(),
        /**
         * Catalog edits made elsewhere that the publish carried into the project's file rather than
         * overwrite (`docs/achievements-plan.md` §5.5). The panel shows it outside the details. Absent
         * when nothing had been edited elsewhere.
         */
        playMerged: PlayMerged.optional(),
    }),
]);
/** One sibling's row, as the job record keeps it. */
export const PublishJobBuild = z.object({
    pieceModel: z.string(),
    primary: z.boolean(),
    position: z.number().int(),
    step: PublishStep,
    buildId: z.string().nullable(),
    bytes: z.number().int().nullable(),
});
export const PublishJob = z.object({
    id: Ulid,
    appId: Ulid,
    kind: PublishJobKind,
    channelId: Ulid,
    channelSlug: z.string(),
    sourceBuildId: z.string().nullable(),
    versionName: z.string().nullable(),
    versionCode: z.number().int().nullable(),
    status: PublishJobStatus,
    /** The job this one waits behind — one at a time per game and target channel. */
    waitingFor: z.string().nullable().optional(),
    error: z.string().nullable(),
    builds: z.array(PublishJobBuild),
    events: z.array(PublishEvent),
    /** The previous finished job's real duration on this game, never an estimate (§6.5). */
    lastDurationMs: z.number().int().nullable().optional(),
    createdAt: z.number().int(),
    startedAt: z.number().int().nullable().optional(),
    finishedAt: z.number().int().nullable(),
    /** When somebody dismissed the finished job's notice on the channel page. */
    seenAt: z.number().int().nullable().optional(),
});
export const PublishJobAccepted = z.object({ jobId: Ulid, status: PublishJobStatus });
/** `POST /v1/publish-jobs/:id/events` — what a job's sandbox (or agentd) reports. */
export const PublishJobEventsRequest = z.object({ events: z.array(PublishEvent).min(1).max(50) });
/** `POST /v1/apps/:appId/publish-jobs` — agentd's interactive publish opening its record. */
export const CreatePublishJobRequest = z.object({
    channel: Slug,
    versionName: z.string().trim().min(1).max(120).nullable().optional(),
    siblings: z.array(PublishSibling).min(1).max(20),
});
/* ---------------------------------------------------------------------------- the format */
/** A model's name as a person knows it; a stem nobody registered is its own name. */
export function pieceModelName(stem) {
    return pieceModelOf(stem)?.display ?? stem;
}
/** `38 s`, `4 min 12 s`, `1 h 2 min`. Whole seconds: nobody waits on a tenth. */
export function formatPublishDuration(ms) {
    const seconds = Math.max(0, Math.round(ms / 1000));
    if (seconds < 60)
        return `${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return seconds % 60 ? `${minutes} min ${seconds % 60} s` : `${minutes} min`;
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}
/** agentd's own sizes (`describeSize`), so the log and the deploy line agree. */
export function formatPublishBytes(bytes) {
    return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
/** `01JBX4…7Q` — the panel's form. The stored log and the CLI keep ids whole. */
export function shortBuildId(id) {
    return id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-2)}` : id;
}
/** The siblings in the order the opening line lists them: by position, the primary last. */
function ordered(siblings) {
    return [...siblings].sort((a, b) => a.position - b.position);
}
function names(siblings) {
    return ordered(siblings)
        .map((one) => one.primary
        ? `${pieceModelName(one.pieceModel)} (primary${siblings.length > 1 ? ', published last' : ''})`
        : pieceModelName(one.pieceModel))
        .join(', ');
}
/** `[2/3 Omakase]`, padded so the step words line up down the log. */
export function siblingLabel(pieceModel, context) {
    const total = context.siblings.length;
    const label = (stem) => {
        const sibling = context.siblings.find((one) => one.pieceModel === stem);
        return `[${sibling?.position ?? '?'}/${total} ${pieceModelName(stem)}]`;
    };
    const width = Math.max(...context.siblings.map((one) => label(one.pieceModel).length));
    return label(pieceModel).padEnd(width + 1);
}
/**
 * One event as the lines a person reads. `start` is two lines (what, then which builds); every
 * other event is one. The **only** formatter: the panel, the stored log, the CLI and agentd call it.
 */
export function formatPublishEvent(event, context) {
    const id = (value) => (context.shortIds ? shortBuildId(value) : value);
    switch (event.type) {
        case 'start': {
            const count = `${event.siblings.length} build${event.siblings.length === 1 ? '' : 's'}`;
            const version = event.versionName ? `${event.versionName} ` : '';
            const head = event.kind === 'rebuild'
                ? `Rebuilding ${version}for ${event.channel}` +
                    (event.from
                        ? ` from the ${event.from.versionName} deploy` +
                            (event.from.builtFor ? ` (built for ${event.from.builtFor})` : '')
                        : '')
                : event.kind === 'copy'
                    ? `Copying ${version}to ${event.channel}`
                    : `Publishing ${version}to ${event.channel}`;
            return [`${head}, ${count}:`, `  ${names(event.siblings)}`];
        }
        case 'step': {
            const label = siblingLabel(event.pieceModel, context);
            const took = event.durationMs != null ? ` (${formatPublishDuration(event.durationMs)})` : '';
            switch (event.step) {
                case 'check':
                    return [`${label}checking`];
                case 'build':
                    return [`${label}building: ${context.channel ? `channel ${context.channel}, ` : ''}pieces ${event.pieceModel}`];
                case 'pack':
                    return [`${label}packed${event.bytes != null ? ` ${formatPublishBytes(event.bytes)}` : ''}${took}`];
                case 'upload':
                    if (event.sourceBuildId && event.buildId) {
                        return [`${label}copied ${id(event.sourceBuildId)} → ${id(event.buildId)}`];
                    }
                    return [
                        `${label}uploaded` +
                            (event.buildId ? ` → build ${id(event.buildId)}` : '') +
                            (event.versionCode != null ? `, version code ${event.versionCode}` : ''),
                    ];
                case 'patch':
                    return [`${label}adding the in-game layer…${event.durationMs != null ? ` done${took}` : ''}`];
                case 'published':
                    return [`${label}published${context.channel ? ` to ${context.channel}` : ''}${event.buildId ? ` as ${id(event.buildId)}` : ''}`];
                case 'failed':
                    return [`${label}failed${event.message ? `: ${event.message}` : ''}`];
            }
            return [];
        }
        case 'synced': {
            // Priority order, the game's list's own (P4). Which one a Board installs is the channel's
            // model choice, so the line says what it holds and not what a Board gets.
            const list = event.models.map(pieceModelName).join(', ');
            return [`${event.channel} now plays with ${list}${event.models.length > 1 ? ', in priority order' : ''}`];
        }
        case 'done': {
            const took = formatPublishDuration(event.durationMs);
            const version = event.versionName != null
                ? ` as ${event.versionName}${event.versionCode != null ? ` (${event.versionCode})` : ''}`
                : '';
            if (event.status === 'failed') {
                return [`Failed after ${took}. Nothing was published${event.error ? `: ${event.error}` : '.'}`];
            }
            const total = context.siblings.length;
            if (event.status === 'partial') {
                const list = (stems) => {
                    const named = (stems ?? []).map(pieceModelName);
                    return named.length <= 1 ? (named[0] ?? 'none') : `${named.slice(0, -1).join(', ')} and ${named.at(-1)}`;
                };
                return [
                    `Partly done in ${took}: ${list(event.published)} published to ${event.channel}${version}; ` +
                        `${list(event.unpublished)} did not` +
                        (event.error ? `: ${event.error}` : '.'),
                ];
            }
            return [
                `Done in ${took}: ${total} build${total === 1 ? '' : 's'} published to ${event.channel}${version}.`,
                ...(event.catalogsNotPushed ?? []).map(catalogNotPushedSentence),
                ...(event.playMerged ? playMergedSentences(event.playMerged) : []),
                ...(event.playOff ? [playOffSentence(event.playOff.services, event.channel)] : []),
            ];
        }
    }
}
/** The publish's sentence about a catalog that did not go up, ending as a sentence does. */
export function catalogNotPushedSentence(entry) {
    const reason = entry.reason.trim();
    return `${entry.file} was not pushed: ${reason}${/[.!?)]$/.test(reason) ? '' : '.'}`;
}
/** Where a catalog version was made, for the merge's sentences. */
const MADE = {
    dashboard: 'on the dashboard',
    cli: 'with the CLI',
    mcp: 'by an agent over MCP',
    unity: 'from the Unity plugin',
    playground: 'by another playground publish',
};
/**
 * The publish's sentences about edits made elsewhere (§5.5): what it brought into the file, then
 * where both had changed the same thing. Neutral about who reads them, as `playOffSentence` is.
 */
export function playMergedSentences(merged) {
    const made = MADE[merged.source];
    const head = `The achievements and leaderboards were edited ${made} (version ${merged.version}) since boardflight.play.json last matched them`;
    if (!merged.written) {
        return [`${head}, but the file changed during this publish, so nothing was pushed. The next publish merges them.`];
    }
    return [
        `${head}, so this publish merged them into the file instead of overwriting them` +
            (merged.brought.length > 0 ? `: ${merged.brought.join('; ')}.` : '.'),
        ...(merged.kept.length > 0 ? [`Where both changed the same thing, the edit made ${made} won: ${merged.kept.join('; ')}.`] : []),
    ];
}
/**
 * The publish's one sentence about play services its channel has off (§8.1). Neutral about where
 * the switch is, because the panel, the CLI and an agent all read it: the panel draws the button.
 */
export function playOffSentence(services, channel) {
    const achievements = services.includes('achievements');
    const leaderboards = services.includes('leaderboards');
    const [named, verb] = achievements && leaderboards
        ? ['Achievements and leaderboards are', 'unlocks and ranks']
        : achievements
            ? ['Achievements are', 'unlocks']
            : ['Leaderboards are', 'ranks'];
    return `${named} off on ${channel}, so on a Board this build ${verb} nothing until a person turns them on there.`;
}
/**
 * A whole job's log. The `start` event carries the siblings every later line is labelled by; a log
 * cut short before it (it never is, but a stored log is data) still renders, with `?` positions.
 */
export function formatPublishLog(events, options = {}) {
    const start = events.find((event) => event.type === 'start');
    const context = {
        siblings: start?.siblings ?? [],
        shortIds: options.shortIds,
        ...(start ? { channel: start.channel } : {}),
    };
    if (context.siblings.length === 0) {
        const seen = [...new Set(events.flatMap((event) => (event.type === 'step' ? [event.pieceModel] : [])))];
        context.siblings = seen.map((pieceModel, index) => ({ pieceModel, primary: false, position: index + 1 }));
    }
    return events.flatMap((event) => formatPublishEvent(event, context));
}
/**
 * Which siblings published and which did not, from the events — what a partial result names.
 * The `done` line says *that* it was partial; this says *who*, for the panel and the CLI.
 */
export function publishOutcome(events) {
    const start = events.find((event) => event.type === 'start');
    const all = ordered(start?.siblings ?? []).map((one) => one.pieceModel);
    const published = new Set(events.flatMap((event) => (event.type === 'step' && event.step === 'published' ? [event.pieceModel] : [])));
    return {
        published: all.filter((stem) => published.has(stem)),
        unpublished: all.filter((stem) => !published.has(stem)),
    };
}
//# sourceMappingURL=publish-jobs.js.map